import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, get, post, type Programme, type RundownDto } from '../api.js';
import { PageTitle } from '../frame.js';
import { t } from '../i18n.js';
import { DateInput } from '../date-input.js';
import { Link, navigate } from '../router.js';
import { fmtDateInput, todayIso } from '../util.js';

interface TodayRow {
  id: string;
  programmeId: string;
  name: string;
  status: string;
  revision: number;
  count: number;
}

export function RundownSelectPage() {
  const [programmes, setProgrammes] = useState<Programme[]>([]);
  const [programmeId, setProgrammeId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [today, setToday] = useState<TodayRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<{ programmes: Programme[] }>('/api/programmes').then((r) => setProgrammes(r.programmes));
    get<{ rundowns: TodayRow[] }>('/api/rundowns/today').then((r) => setToday(r.rundowns));
  }, []);

  const search = async (e: FormEvent) => {
    e.preventDefault();
    if (!programmeId || !date) return setError(t('tipRequired'));
    try {
      const r = await post<{ rundown: RundownDto }>('/api/rundowns/open', { programmeId, date });
      navigate(`/rundown/${r.rundown.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const form = (
    <form onSubmit={search} className="panel">
      <div className="toolbar">
        <button type="button" className="btn" onClick={() => (setProgrammeId(''), setDate(todayIso()))}>
          ↺ {t('clear')}
        </button>
        <button className="btn primary">🔎 {t('search')}</button>
      </div>
      <div className="panel-title">{t('bulletinDetails')}</div>
      <div className="form-grid" style={{ gridTemplateColumns: 'max-content 1fr' }}>
        <label className="req">{t('bulletin')} :</label>
        <select value={programmeId} onChange={(e) => setProgrammeId(e.target.value)}>
          <option value="">{t('choose')}</option>
          {programmes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <label className="req">{t('bulletinDate')} :</label>
        <DateInput value={date} onChange={setDate} />
      </div>
      <div className="toolbar">
        <button type="button" className="btn" onClick={() => (setProgrammeId(''), setDate(todayIso()))}>
          ↺ {t('clear')}
        </button>
        <button className="btn primary">🔎 {t('search')}</button>
      </div>
    </form>
  );

  return (
    <>
      <PageTitle title={t('rundownTitle')} hint={t('rundownSelectHint')} />
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        {form}
        {error && <div className="banner err">{error}</div>}
        <p className="tip">{t('tipRequired')}</p>
        {today.length > 0 && (
          <div className="panel">
            <div className="panel-title">
              {t('todaysRundowns')} · {fmtDateInput(todayIso())}
            </div>
            <table className="grid" style={{ margin: 6, width: 'calc(100% - 12px)' }}>
              <thead>
                <tr>
                  <th>{t('bulletin')}</th>
                  <th>{t('newsCount')}</th>
                  <th>{t('revision')}</th>
                  <th>Κατάσταση</th>
                </tr>
              </thead>
              <tbody>
                {today.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/rundown/${r.id}`}>{r.name}</Link>
                    </td>
                    <td className="num">{r.count}</td>
                    <td className="num">{r.revision}</td>
                    <td className="c">
                      <span className={`status ${r.status === 'published' ? 'ok' : r.status === 'linked' ? 'info' : ''}`}>{t(r.status as 'draft' | 'linked' | 'published')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
